import './faq.scss';

/**
 * Accordion controller for the FAQ area.
 *
 * Manages the expanded/collapsed state of additional FAQ items (starting from the 6th item)
 * by toggling ARIA attributes and data attributes for accessibility and styling.
 *
 * Usage: instantiate with the accordion root element and call initialize().
 * The class expects the following DOM structure inside the root:
 * - A CTA element matching '.faq__accordion-cta .button'
 * - Hidden items matching '.faq__accordion-list > .faq__accordion-item:nth-child(n+6)'
 */
class Faq {
    /**
     * @param {HTMLElement} $element The accordion root element.
     */
    constructor ($element) {
        /** @type {HTMLElement} */
        this.$root = $element;
        /** @type {HTMLElement|null} */
        this.$button = this.$root.querySelector('.faq__accordion-cta .button');
        /** @type {NodeListOf<HTMLElement>} */
        this.$$items = this.$root.querySelectorAll('.faq__accordion-list > .faq__accordion-item:nth-child(n+6)');
    }

    /**
     * Initialize the accordion behavior by binding events and syncing initial state.
     * Safe to call multiple times; event handler is attached once per instance lifetime.
     *
     * @returns {void}
     */
    initialize () {
        if (!this.$button || !this.$$items.length) return;
        this.setOpen(this.isOpen());
        this.$button.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggle();
        }, { once: false });
    }

    /**
     * Determine whether the accordion is currently open.
     * Prefers the explicit aria-expanded state, falling back to data-readmore-expanded on the root.
     *
     * @returns {boolean} True if open; otherwise false.
     */
    isOpen () {
        const expanded = this.$button?.getAttribute('aria-expanded');
        if (expanded !== null) return expanded === 'true';
        const state = this.$root.getAttribute('data-readmore-expanded');
        return state === 'true';
    }

    /**
     * Set the open/closed state and update all relevant attributes for accessibility.
     *
     * @param {boolean} open Desired open state.
     * @returns {void}
     */
    setOpen (open) {
        // Toggle ARIA and dataset
        this.$root.setAttribute('data-readmore-expanded', open ? 'true' : 'false');
        this.$button?.setAttribute('aria-expanded', String(open));
        // Toggle aria-hidden on hidden items for accessibility
        this.$$items.forEach((el) => el.setAttribute('aria-hidden', open ? 'false' : 'true'));
    }

    /**
     * Toggle the current open/closed state.
     * @returns {void}
     */
    toggle () {
        this.setOpen(!this.isOpen());
    }
}

export { Faq };

// Initialize all FAQ accordions when the custom content.loaded event fires
window.addEventListener('content.loaded', (e) => {
    const { $context } = e.detail ?? {};
    if (!$context) return;

    const $$roots = $context.querySelectorAll('.faq__accordion[data-accordion="root"]');
    if (!$$roots.length) return;

    $$roots.forEach((root) => {
        const $root = new Faq(root);
        $root.initialize();
    });
});
